import { gql } from '@apollo/client'

export const CREATE_TEAM = gql`
  mutation CreateTeam($newTeam: newTeam!) {
    createTeam(newTeam: $newTeam) {
      returnCode
      message
      id
    }
  }
`

export const CREATE_TEAM_ADMIN = gql`
  mutation CreateTeamAdmin($newTeamAdmin: newTeamAdmin!) {
    createTeamAdmin(newTeamAdmin: $newTeamAdmin) {
      returnCode
      message
    }
  }
`

export const UPDATE_TEAM = gql`
  mutation UpdateTeam($changedTeam: changedTeam!) {
    updateTeam(changedTeam: $changedTeam) {
      returnCode
      message
      id
    }
  }
`

// Hard-blocks server-side if the group still has profiles in it (BAC-1824) — the caller must
// reassign them via REASSIGN_SUB_PROFILES_OF_TEAM first. See DeleteGroupModal.
export const DELETE_TEAM = gql`
  mutation DeleteTeam($deleteTeamId: ID!) {
    deleteTeam(deleteTeamId: $deleteTeamId) {
      returnCode
      message
      id
    }
  }
`

// Bulk re-parents every profile currently in `teamId` (via their personal team's teamParentId —
// see BAC-1823) into `newTeamId` instead. Caller must be a team admin of BOTH groups.
export const REASSIGN_SUB_PROFILES_OF_TEAM = gql`
  mutation ReassignSubProfilesOfTeam($teamId: ID!, $newTeamId: ID!) {
    reassignSubProfilesOfTeam(teamId: $teamId, newTeamId: $newTeamId) {
      returnCode
      message
    }
  }
`

export const CREATE_TEAM_BRANDING = gql`
  mutation CreateTeamBranding($newTeamBranding: newTeamBranding!) {
    createTeamBranding(newTeamBranding: $newTeamBranding) {
      returnCode
      message
      id
    }
  }
`

export const UPDATE_TEAM_BRANDING = gql`
  mutation UpdateTeamBranding($changedTeamBranding: changedTeamBranding!) {
    updateTeamBranding(changedTeamBranding: $changedTeamBranding) {
      returnCode
      message
      id
    }
  }
`

export const CREATE_TEAM_BRANDING_ASSET = gql`
  mutation CreateTeamBrandingAsset($newTeamBrandingAsset: newTeamBrandingAsset!) {
    createTeamBrandingAsset(newTeamBrandingAsset: $newTeamBrandingAsset) {
      returnCode
      message
      id
    }
  }
`

export const UPDATE_TEAM_BRANDING_ASSET = gql`
  mutation UpdateTeamBrandingAsset($changedTeamBrandingAsset: changedTeamBrandingAsset!) {
    updateTeamBrandingAsset(changedTeamBrandingAsset: $changedTeamBrandingAsset) {
      returnCode
      message
      id
    }
  }
`

// A "never expires" link sends a ~100-year expiresInDays (see AccountAdminsModal's
// NEVER_EXPIRES_DAYS) rather than omitting the arg — expiresInDays stays required, no backend
// schema change needed for that part. role is internal-admin-only on the frontend — see
// AccountAdminsModal — but the backend independently enforces that only an internal/salesRep
// caller may request a non-'admin' role (BAC-1834), since hiding a UI control isn't a security
// boundary. role is typed String (not a GraphQL enum) because the backend's typeDef declares it
// as String and validates the value manually against the Prisma enum at runtime — there's no
// TeamAdminRoleOptions enum in the actual GraphQL schema.
export const GENERATE_TEAM_ADMIN_INVITE_LINK = gql`
  mutation GenerateTeamAdminInviteLink($teamId: ID!, $expiresInDays: Int!, $role: String) {
    generateTeamAdminInviteLink(teamId: $teamId, expiresInDays: $expiresInDays, role: $role) {
      id
      code
      createdAt
      expiresAt
      revoked
      redemptionCount
      role
    }
  }
`

export const REVOKE_TEAM_ADMIN_INVITE_LINK = gql`
  mutation RevokeTeamAdminInviteLink($id: ID!) {
    revokeTeamAdminInviteLink(id: $id) {
      returnCode
      message
    }
  }
`

// Authenticated with the caller's own real sign-in token (not the anonymous session) — must run
// after real sign-in/sign-up resolves. See LoginPage.
export const REDEEM_TEAM_ADMIN_INVITE = gql`
  mutation RedeemTeamAdminInvite($code: String!) {
    redeemTeamAdminInvite(code: $code) {
      returnCode
      message
    }
  }
`

// Front-door signup's self-serve team creation (BAC-1760, ADM-616) — creates a top-level team
// (named from companyName, or a "{firstName}'s Company"/"Your Company" default server-side),
// makes the caller its admin, and creates a personal sub-team under it, all in one call. Only
// usable by an authenticated caller with zero existing team_admins rows — see SignupPage.
// `brand: true` tags the new team ["Brand"] so useTeamStore's isRealEstate check (which
// defaults to RE when tags don't include "Brand") correctly classifies it — this signup flow
// doesn't support RE self-serve yet, so it's always true here.
export const CREATE_TEAM_FOR_NEW_SIGNUP = gql`
  mutation CreateTeamForNewSignup($companyName: String, $brand: Boolean, $turnstileToken: String) {
    createTeamForNewSignup(companyName: $companyName, brand: $brand, turnstileToken: $turnstileToken) {
      returnCode
      message
      id
    }
  }
`
